/**
 * @file ContentTagsPage.tsx
 * @description CRUD тегов блога (phase12.0).
 */

import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Input } from "../../../components/ui/input";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { contentTags, type ContentTag } from "../../../lib/api-content";

export function ContentTagsPage() {
  const notify = useNotify();
  const [tags, setTags] = useState<ContentTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setTags(await contentTags.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setSubmitting(true);
    try {
      await contentTags.create({ name: newName.trim() });
      setNewName("");
      notify.success("Тег создан");
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось создать");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(t: ContentTag) {
    if (!confirm(`Удалить тег «${t.name}»?`)) return;
    try {
      await contentTags.remove(t.id);
      await refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Контент · Теги</div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Теги блога</h1>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body>
          <form onSubmit={(e) => void onCreate(e)} className="flex gap-2 mb-4">
            <Input
              placeholder="Например: «иван-чай», «фитотерапия»"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={submitting}
              className="flex-1"
            />
            <Button type="submit" disabled={submitting || !newName.trim()}>
              Добавить
            </Button>
          </form>

          {loading ? (
            <p className="text-sm text-on-surface-muted">Загрузка…</p>
          ) : tags.length === 0 ? (
            <p className="text-sm text-on-surface-muted">Тегов нет.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <div
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-full bg-on-surface/8 px-3 py-1 text-sm"
                >
                  <span>{t.name}</span>
                  {t.articlesCount !== undefined && (
                    <span className="text-on-surface-muted text-xs">
                      ({t.articlesCount})
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => void onDelete(t)}
                    className="ml-1 text-on-surface-muted hover:text-rose-600"
                    aria-label={`Удалить ${t.name}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </EditorialCard.Body>
      </EditorialCard>
    </div>
  );
}
